/**
 * Unified chat (WhatsApp-style) data hooks.
 *
 * These call the api-alpha `/v1/chat/*` routes directly through the shared
 * `client` wrapper (the same approach as `useSupportTickets.ts` /
 * `useContactSupport.ts`) because the endpoints are not in the generated
 * OpenAPI SDK yet and regenerating requires a running backend. Envelope handling
 * mirrors the generated-SDK hooks.
 *
 * List query-param names are the NON-prefixed DB business-param names
 * (`limit`, `before_last_message_posted_at`, `before_channel_id`,
 * `include_archived`, `offset`, `before_posted_at`, `before_id`) — the api-alpha
 * handlers map them to the DB `p_*` params. Verified against api-alpha
 * `src/routes/chat/chat.ts`.
 *
 * Live updates arrive over the shared WebSocket via `useChatRealtime`, which
 * invalidates the keys below on `chat.message.*` events. TanStack Query's
 * refetch-on-focus, a slow backstop `refetchInterval` on the open channel, and
 * invalidate-on-mutate remain as fallbacks when the socket is briefly down.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { client } from "@/services/api/client.gen";
import type {
  ApiEnvelope,
  ChatChannelListItem,
  ChatMessage,
  ChatVisibility,
} from "@/types/chat";

/**
 * Query-key builders. Exported so the WebSocket live-append consumer
 * (`useChatRealtime`) can invalidate exactly these keys without drifting from
 * the shapes used here.
 */
export const CHATS_ROOT_KEY = ["chat", "chats"] as const;
export const channelMessagesKey = (channelId: string) =>
  ["chat", "channel", channelId, "messages"] as const;

const DEFAULT_CHATS_LIMIT = 50;
const DEFAULT_MESSAGES_LIMIT = 50;

/**
 * Fallback poll for the open channel. WebSocket `chat.message.*` events are now
 * the primary path for live updates (see `useChatRealtime`); this slow interval
 * is only a backstop for when the socket is briefly down.
 */
const OPEN_CHANNEL_REFETCH_MS = 20_000;

/**
 * The caller's WhatsApp-style chat list, newest-message-first.
 * `includeArchived` toggles whether archived channels are returned.
 */
export function useChats(
  includeArchived = false,
): UseQueryResult<ChatChannelListItem[]> {
  return useQuery({
    queryKey: [...CHATS_ROOT_KEY, { includeArchived }],
    queryFn: async () => {
      const result = await client.get({
        url: "/v1/chat/chats",
        query: {
          limit: DEFAULT_CHATS_LIMIT,
          include_archived: includeArchived ? "true" : "false",
        },
        throwOnError: true,
      });
      const envelope = result.data as
        ApiEnvelope<ChatChannelListItem[]> | undefined;
      return envelope?.data ?? [];
    },
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * The message thread for a channel (DB returns `posted_at DESC`). Disabled until
 * a channel id is provided. Polls while open so replies appear promptly.
 */
export function useChannelMessages(
  channelId: string | null,
): UseQueryResult<ChatMessage[]> {
  return useQuery({
    queryKey: channelId
      ? channelMessagesKey(channelId)
      : ["chat", "channel", null, "messages"],
    enabled: Boolean(channelId),
    queryFn: async () => {
      const result = await client.get({
        url: `/v1/chat/channels/${channelId}/messages`,
        query: { limit: DEFAULT_MESSAGES_LIMIT, offset: 0 },
        throwOnError: true,
      });
      const envelope = result.data as ApiEnvelope<ChatMessage[]> | undefined;
      return envelope?.data ?? [];
    },
    staleTime: 5 * 1000,
    refetchInterval: OPEN_CHANNEL_REFETCH_MS,
    refetchOnWindowFocus: true,
  });
}

/** Post a markdown message to a channel, then refetch the thread + chat list. */
export function usePostChatMessage(channelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (content: string) => {
      const result = await client.post({
        url: `/v1/chat/channels/${channelId}/messages`,
        body: { content },
        throwOnError: true,
      });
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: channelMessagesKey(channelId),
      });
      void queryClient.invalidateQueries({ queryKey: CHATS_ROOT_KEY });
    },
  });
}

/**
 * Mark a channel read up to a message (zeroes unread, sets the watermark).
 * Fire-and-forget on open; refresh the chat list so the unread badge clears.
 */
export function useMarkChatRead(channelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (lastReadMessageId: string) => {
      const result = await client.post({
        url: `/v1/chat/channels/${channelId}/read`,
        body: { lastReadMessageId },
        throwOnError: true,
      });
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHATS_ROOT_KEY });
    },
  });
}

/** Archive / unarchive a channel from the caller's chat list. */
export function useSetChatVisibility(channelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (visibility: ChatVisibility) => {
      const result = await client.post({
        url: `/v1/chat/channels/${channelId}/visibility`,
        body: { visibility },
        throwOnError: true,
      });
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHATS_ROOT_KEY });
    },
  });
}
